import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import Modal from './Modal'
import {
  registerConfirmHost,
  registerPromptHost,
  registerToastHost,
  type ConfirmOptions,
  type PromptOptions,
  type ToastItem,
  type ToastVariant,
} from '../../lib/dialogs'

// Host único de los diálogos de la app (ver lib/dialogs.ts). Se monta una sola
// vez en main.tsx, por debajo de i18n, para que los textos por defecto de los
// botones salgan en el idioma de la aplicación y no en el del navegador.

interface PendingConfirm {
  opts: ConfirmOptions
  resolve: (value: boolean) => void
}

interface PendingPrompt {
  opts: PromptOptions
  resolve: (value: string | null) => void
}

const TOAST_MS: Record<ToastVariant, number> = {
  // Un error hay que poder leerlo entero; un "hecho" estorba si se queda.
  error: 7000,
  success: 4000,
  info: 5000,
}

const TOAST_STYLE: Record<ToastVariant, { icon: typeof Info; accent: string }> = {
  error: { icon: AlertTriangle, accent: 'text-rose-500' },
  success: { icon: CheckCircle2, accent: 'text-emerald-500' },
  info: { icon: Info, accent: 'text-sky-500' },
}

export default function DialogHost() {
  const { t } = useTranslation('common')
  const [pending, setPending] = useState<PendingConfirm | null>(null)
  const [prompting, setPrompting] = useState<PendingPrompt | null>(null)
  const [promptValue, setPromptValue] = useState('')
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null)
  const promptInputRef = useRef<HTMLInputElement | null>(null)
  const nextId = useRef(1)
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  // ---------- Confirmación ----------
  useEffect(() => {
    registerConfirmHost(
      (opts) =>
        new Promise<boolean>((resolve) => {
          setPending((current) => {
            // Si ya había una confirmación en pantalla, la damos por cancelada
            // en vez de sustituirla y dejar su promesa colgada: el `await` del
            // llamante no volvería NUNCA y su `finally` (spinners, setSaving)
            // tampoco, dejando el botón girando para siempre.
            current?.resolve(false)
            return { opts, resolve }
          })
        })
    )
    return () => registerConfirmHost(null)
  }, [])

  const settle = useCallback(
    (value: boolean) => {
      setPending((current) => {
        current?.resolve(value)
        return null
      })
    },
    []
  )

  // Enter confirma sin tener que apuntar con el ratón (como el diálogo nativo).
  useEffect(() => {
    if (pending) confirmBtnRef.current?.focus()
  }, [pending])

  // Escape cancela SOLO la confirmación, no lo que haya debajo. `Modal` escucha
  // el teclado en `document`, así que con una confirmación abierta sobre otro
  // modal (borrar un adjunto del asiento, dar de baja un inmovilizado, quitar
  // una capacidad de usuario…) una sola pulsación cerraría los dos y el usuario
  // perdería el formulario por cancelar un borrado. Escuchamos en fase de
  // captura y cortamos la propagación: el listener del modal de debajo —y el de
  // este mismo— ya no llegan a ejecutarse, así que cerramos nosotros.
  useEffect(() => {
    if (!pending) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      settle(false)
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [pending, settle])

  // ---------- Petición de un texto (sustituye a window.prompt) ----------
  useEffect(() => {
    registerPromptHost(
      (opts) =>
        new Promise<string | null>((resolve) => {
          setPromptValue(opts.defaultValue ?? '')
          setPrompting((current) => {
            // Misma razón que en la confirmación: no dejamos colgada la promesa
            // de una petición anterior que quedara sin contestar.
            current?.resolve(null)
            return { opts, resolve }
          })
        })
    )
    return () => registerPromptHost(null)
  }, [])

  const settlePrompt = useCallback((value: string | null) => {
    setPrompting((current) => {
      current?.resolve(value)
      return null
    })
  }, [])

  // El nativo abre con el valor por defecto seleccionado: escribir lo reemplaza.
  useEffect(() => {
    if (prompting) promptInputRef.current?.select()
  }, [prompting])

  // Escape cancela solo esta petición (ver el comentario de la confirmación).
  useEffect(() => {
    if (!prompting) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      settlePrompt(null)
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [prompting, settlePrompt])

  // ---------- Avisos ----------
  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
    setToasts((rows) => rows.filter((r) => r.id !== id))
  }, [])

  useEffect(() => {
    registerToastHost((message, variant) => {
      const id = nextId.current++
      setToasts((rows) => [...rows, { id, message, variant }])
      timers.current.set(
        id,
        setTimeout(() => {
          timers.current.delete(id)
          setToasts((rows) => rows.filter((r) => r.id !== id))
        }, TOAST_MS[variant])
      )
    })
    return () => registerToastHost(null)
  }, [])

  // Al desmontar (recarga, logout) no dejamos timers vivos apuntando a setState.
  const allTimers = timers.current
  useEffect(() => () => allTimers.forEach((timer) => clearTimeout(timer)), [allTimers])

  const tone = pending?.opts.tone ?? 'default'

  return (
    <>
      <Modal
        open={pending !== null}
        onClose={() => settle(false)}
        title={pending?.opts.title ?? t('confirm.defaultTitle')}
        maxWidth="max-w-md"
      >
        <p className="text-sm t-secondary whitespace-pre-line">{pending?.opts.message}</p>
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={() => settle(false)}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            {pending?.opts.cancelLabel ?? t('actions.cancel')}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={() => settle(true)}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold text-white ${
              tone === 'danger'
                ? 'bg-rose-600 hover:bg-rose-500'
                : 'bg-jr2-600 hover:bg-jr2-700'
            }`}
          >
            {pending?.opts.confirmLabel ?? t('actions.confirm')}
          </button>
        </div>
      </Modal>

      <Modal
        open={prompting !== null}
        onClose={() => settlePrompt(null)}
        title={prompting?.opts.title ?? t('confirm.defaultTitle')}
        maxWidth="max-w-md"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            settlePrompt(promptValue)
          }}
        >
          <p className="text-sm t-secondary whitespace-pre-line">{prompting?.opts.message}</p>
          <input
            ref={promptInputRef}
            type="text"
            value={promptValue}
            onChange={(e) => setPromptValue(e.target.value)}
            placeholder={prompting?.opts.placeholder}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-3"
          />
          <div className="flex justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={() => settlePrompt(null)}
              className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
            >
              {prompting?.opts.cancelLabel ?? t('actions.cancel')}
            </button>
            <button
              type="submit"
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700"
            >
              {prompting?.opts.confirmLabel ?? t('actions.confirm')}
            </button>
          </div>
        </form>
      </Modal>

      {toasts.length > 0 &&
        createPortal(
          <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 w-[min(24rem,calc(100vw-2rem))]">
            {toasts.map((item) => {
              const { icon: Icon, accent } = TOAST_STYLE[item.variant]
              return (
                <div
                  key={item.id}
                  role={item.variant === 'error' ? 'alert' : 'status'}
                  className="surface-deep p-3 pr-2 flex items-start gap-2.5 animate-slideUp"
                >
                  <Icon size={16} className={`${accent} mt-0.5 shrink-0`} />
                  <p className="text-sm t-primary flex-1 min-w-0 break-words whitespace-pre-line">
                    {item.message}
                  </p>
                  <button
                    type="button"
                    onClick={() => dismiss(item.id)}
                    className="w-6 h-6 grid place-items-center rounded-md t-muted hover:t-primary hover:bg-hover shrink-0"
                    aria-label={t('actions.close')}
                  >
                    <X size={14} />
                  </button>
                </div>
              )
            })}
          </div>,
          document.body
        )}
    </>
  )
}
